import { useCallback, useEffect, useRef, useState } from 'react';
import { openChannel, liveAvailable, LIVE_TRANSPORT } from './liveChannel.js';
import { fingerprint } from './assessmentPages.js';

// The screen and the iPad, kept in step.
//
// The advisor opens the assessment on the screen the prospect watches, scans a
// code, and the same assessment opens on their iPad (Jimmy, 2026-10-03). From
// then on the two are one meeting: turning a page on either turns it on both,
// and a line drawn on the iPad appears on the screen as it is drawn.
//
// The screen is the host and the only one that saves. The iPad is a companion:
// it shows exactly what the host holds, sends back what the advisor does on it
// (a page turned, a stroke, an undo), and never writes to Salesforce or to its
// own storage. So there is one writer however many devices are open, and
// nothing the iPad does can be lost or doubled by a save from the other side.
//
// What passes between them:
//   hello   companion → host   "I am here", with a digest of what it is showing
//   alive   host → companion   "so am I"
//   state   host → companion   everything the pages are drawn from, and where the meeting is
//   nav     either way         a page was turned
//   live    either way         the line being drawn right now, a few new points at a time
//   ink     companion → host   a finished stroke, an undo, a clear
//
// On a deployed site every one of these is a message through a service that
// counts them, so the line in progress is sent as the points added since the
// last message rather than the whole line again, and the heartbeat is slower
// than it is on a developer's machine.
//
// Three things keep the two from drifting apart.
//
// A page turn carries a count that only goes up. A message about where the
// meeting was, arriving after the page has been turned again, has a lower
// count and is ignored, so an old position can never pull a device back. When
// both turn a page in the same instant the counts tie and the screen's stands.
//
// The companion keeps every stroke it has sent until the host says it has it,
// and sends again any the host has not acknowledged, so a stroke made while
// the screen was reloading still arrives.
//
// And the host answers a hello whose digest differs from its own by sending
// the state again, so anything else that was missed corrects itself within a
// heartbeat.

const METERED = LIVE_TRANSPORT === 'webpubsub';
const HEARTBEAT_MS = METERED ? 8000 : 3000;
const SEEKING_MS = 1000;
const GONE_MS = METERED ? 20000 : 9000;
const STATE_DEBOUNCE_MS = 120;
const LIVE_MS = METERED ? 45 : 33;
const RESEND_MS = 1500;

const sameBoard = (a, b) => (a || null) === (b || null);

/**
 * The part of the line in progress that has not been sent yet: its new points
 * and where they go. `sent` is how many points of this same line have gone
 * already; a line shorter than that is a different line, sent from its start.
 */
export function lineDelta(live, sent) {
  const p = live.stroke.p;
  const at = sent > p.length ? 0 : sent;
  return {
    part: { step: live.step, board: live.board || null, c: live.stroke.c, w: live.stroke.w, at, p: p.slice(at) },
    count: p.length,
  };
}

/**
 * The other side's line so far, with the part that just arrived added to it.
 * A part that starts at the beginning, belongs to another page, or does not
 * follow on from what is held starts the line again from itself, so a missed
 * message costs the start of one line and nothing else.
 */
export function growLine(line, part) {
  if (!part) return null;
  const follows = line && part.at > 0 && line.step === part.step && sameBoard(line.board, part.board) && line.stroke.p.length === part.at;
  return {
    step: part.step,
    board: part.board || null,
    stroke: { c: part.c, w: part.w, p: follows ? [...line.stroke.p, ...part.p] : part.p },
  };
}

/**
 * Whether a page turn from the other side is newer than where this side is.
 * Equal counts mean both turned at once, and the screen's turn stands.
 */
export function navWins(incoming, mine, role) {
  const v = Number(incoming) || 0;
  return role === 'host' ? v > mine : v >= mine;
}

/**
 * The strokes still owed to the host, given the last one it acknowledged, and
 * which of those have waited long enough to be sent again.
 */
export function settleInk(pending, acked, now, resendAfter = RESEND_MS) {
  const owed = pending.filter(op => op.seq > acked);
  return { owed, again: owed.filter(op => now - op.at >= resendAfter) };
}

/** The pairing code this page was opened with, when it was opened from the code on the screen. */
export function pairCodeFromUrl() {
  try {
    return (new URLSearchParams(window.location.search).get('pair') || '').trim();
  } catch {
    return '';
  }
}

const ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

/** A code nobody could guess: sixteen characters from a browser's own random source. */
export function newPairCode() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => ALPHABET[b % ALPHABET.length]).join('');
}

/** The address the iPad opens: this assessment, this household, and the code. */
export function pairUrl({ origin, pathname, leadId, code, slides = false }) {
  const params = new URLSearchParams();
  if (leadId) params.set('leadId', leadId);
  // A screen showing the October slides (?slides=1) has an iPad that shows
  // them too, so both have the same pages to turn.
  if (slides) params.set('slides', '1');
  params.set('pair', code);
  return `${String(origin || '').replace(/\/$/, '')}${pathname || '/assessment'}?${params.toString()}`;
}

/**
 * What the companion is given: everything the pages are drawn from. The saved
 * copy of a page that each drawing keeps for printing is left behind, because
 * the companion never prints and they are most of the weight.
 */
export function mirrorOf(data) {
  const list = data?.assessment?.sketches?.illustrations;
  if (!Array.isArray(list) || !list.some(i => i && i.capture)) return data;
  return {
    ...data,
    assessment: {
      ...data.assessment,
      sketches: {
        ...data.assessment.sketches,
        illustrations: list.map(i => {
          if (!i || !i.capture) return i;
          const { capture, ...rest } = i;
          return rest;
        }),
      },
    },
  };
}

export function digestOf(data) {
  try {
    return fingerprint(JSON.stringify(mirrorOf(data) ?? null));
  } catch {
    return '';
  }
}

/**
 * @param role    'host', 'companion', or null when this page is neither
 * @param room    the pairing code
 * @param data    the store (the host sends it; the companion's is compared with it)
 * @param nav     where the meeting is: { mode, step, board }
 * @param onNav   the other side turned a page
 * @param onData  companion only: the host's state arrived. The second argument
 *                is false while a stroke of this device's own is still on its
 *                way, when the host's drawings must not replace the local ones.
 * @param onInk   host only: the companion finished a stroke, or took one back
 * @param onLive  the line the other side is drawing, or null when it stops
 */
export default function usePairing({ role, room, data, nav, onNav, onData, onInk, onLive }) {
  const [peers, setPeers] = useState(0);
  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState(false);

  // Whether two devices can be linked here at all. The dev server always can;
  // a deployed site asks its API once, and is null until it has answered.
  const [available, setAvailable] = useState(LIVE_TRANSPORT === 'dev-relay' ? true : LIVE_TRANSPORT ? null : false);
  useEffect(() => {
    if (LIVE_TRANSPORT !== 'webpubsub') return undefined;
    let on = true;
    liveAvailable().then(v => { if (on) setAvailable(v); });
    return () => { on = false; };
  }, []);

  const chan = useRef(null);
  const latest = useRef({});
  latest.current = { data, nav, onNav, onData, onInk, onLive };
  const seen = useRef(new Map());
  const acks = useRef({});
  const hostSeen = useRef(0);
  const seq = useRef(0);
  const pending = useRef([]);
  const lastNav = useRef('');
  const navV = useRef(0);
  const gotState = useRef(false);
  const theirLine = useRef(null);
  const lineSent = useRef({ key: '', count: 0 });

  const sendState = useCallback(() => {
    const c = chan.current;
    if (!c) return;
    const now = latest.current;
    // A page turned here and not yet announced is announced by this.
    const key = JSON.stringify(now.nav);
    if (key !== lastNav.current) {
      lastNav.current = key;
      navV.current += 1;
    }
    c.send('state', { data: mirrorOf(now.data), nav: now.nav, v: navV.current, acks: acks.current });
  }, []);

  useEffect(() => {
    if (!role || !room) return undefined;
    seen.current = new Map();
    acks.current = {};
    gotState.current = false;
    hostSeen.current = 0;
    seq.current = 0;
    pending.current = [];
    navV.current = 0;
    lastNav.current = '';

    const c = openChannel(room, msg => {
      const now = Date.now();
      const L = latest.current;
      if (role === 'host') {
        if (msg.type === 'hello') {
          const fresh = !seen.current.has(msg.from);
          seen.current.set(msg.from, now);
          setPeers(seen.current.size);
          if (fresh || msg.need || msg.digest !== digestOf(L.data)) sendState();
        } else if (msg.type === 'bye') {
          seen.current.delete(msg.from);
          setPeers(seen.current.size);
        } else if (msg.type === 'nav') {
          if (!navWins(msg.v, navV.current, 'host')) return;
          navV.current = Number(msg.v) || 0;
          lastNav.current = JSON.stringify(msg.nav);
          L.onNav?.(msg.nav);
        } else if (msg.type === 'ink') {
          // One sent twice (it was not acknowledged in time) is applied once.
          if (msg.seq <= (acks.current[msg.from] || 0)) return;
          acks.current = { ...acks.current, [msg.from]: msg.seq };
          L.onInk?.(msg);
        } else if (msg.type === 'live') {
          theirLine.current = growLine(theirLine.current, msg.live || null);
          L.onLive?.(theirLine.current);
        }
        return;
      }
      // The companion listens to the host only.
      if (msg.type === 'state' || msg.type === 'alive') {
        hostSeen.current = now;
        setConnected(true);
      }
      if (msg.type === 'state') {
        const first = !gotState.current;
        gotState.current = true;
        setJoined(true);
        // What the host has of this device's strokes, and what it is still owed.
        const { owed, again } = settleInk(pending.current, msg.acks?.[c.id] || 0, now);
        pending.current = owed;
        for (const op of again) {
          op.at = now;
          c.send('ink', op.body);
        }
        L.onData?.(msg.data, owed.length === 0);
        // Where the meeting is, unless this device has turned the page since.
        if (first || navWins(msg.v, navV.current, 'companion')) {
          navV.current = Number(msg.v) || 0;
          lastNav.current = JSON.stringify(msg.nav);
          L.onNav?.(msg.nav);
        }
      } else if (msg.type === 'nav') {
        if (!navWins(msg.v, navV.current, 'companion')) return;
        navV.current = Number(msg.v) || 0;
        lastNav.current = JSON.stringify(msg.nav);
        L.onNav?.(msg.nav);
      } else if (msg.type === 'live') {
        theirLine.current = growLine(theirLine.current, msg.live || null);
        L.onLive?.(theirLine.current);
      } else if (msg.type === 'closing') {
        hostSeen.current = 0;
        setConnected(false);
      }
    });
    chan.current = c;
    if (!c) return undefined;

    let timer = null;
    const beat = () => {
      const now = Date.now();
      if (role === 'host') {
        c.send('alive');
        for (const [id, at] of seen.current) if (now - at > GONE_MS) seen.current.delete(id);
        setPeers(seen.current.size);
      } else {
        c.send('hello', { need: !gotState.current, digest: digestOf(latest.current.data) });
        if (hostSeen.current && now - hostSeen.current > GONE_MS) setConnected(false);
      }
      // Until the host has answered, the companion asks more often.
      timer = setTimeout(beat, role === 'companion' && !gotState.current ? SEEKING_MS : HEARTBEAT_MS);
    };
    beat();

    const leaving = () => c.send(role === 'host' ? 'closing' : 'bye');
    window.addEventListener('pagehide', leaving);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pagehide', leaving);
      leaving();
      c.close();
      chan.current = null;
      setPeers(0);
      setConnected(false);
      setJoined(false);
    };
  }, [role, room, sendState]);

  // The host tells the companion whenever anything it shows has changed.
  useEffect(() => {
    if (role !== 'host' || !peers) return undefined;
    const t = setTimeout(sendState, STATE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [role, peers, data, sendState]);

  // A page turned here is turned there. One that arrived from there is not
  // sent back, and a companion says nothing until it has been told where the
  // meeting is.
  const navKey = JSON.stringify(nav);
  useEffect(() => {
    const c = chan.current;
    if (!c || !role) return;
    if (role === 'companion' && !gotState.current) return;
    if (navKey === lastNav.current) return;
    lastNav.current = navKey;
    navV.current += 1;
    c.send('nav', { nav: JSON.parse(navKey), v: navV.current });
  }, [navKey, role, joined, peers]);

  const sendInk = useCallback(body => {
    const c = chan.current;
    if (!c) return;
    seq.current += 1;
    const op = { seq: seq.current, at: Date.now(), body: { ...body, seq: seq.current } };
    pending.current = [...pending.current, op];
    c.send('ink', op.body);
  }, []);

  // The line in progress, a few dozen times a second at most, and only the
  // points added since the last time. The last word (null, when the pen
  // lifts) always goes at once.
  const liveTimer = useRef(null);
  const liveAt = useRef(0);
  const livePending = useRef(null);
  const sendLive = useCallback(live => {
    const c = chan.current;
    if (!c) return;
    livePending.current = live;
    const flush = () => {
      liveTimer.current = null;
      liveAt.current = Date.now();
      const line = livePending.current;
      if (!line) {
        lineSent.current = { key: '', count: 0 };
        chan.current?.send('live', { live: null });
        return;
      }
      // Which line this is: where it is, and where it began.
      const first = line.stroke.p[0] || [];
      const key = `${line.step}|${line.board || ''}|${first[0]},${first[1]}`;
      const { part, count } = lineDelta(line, lineSent.current.key === key ? lineSent.current.count : 0);
      lineSent.current = { key, count };
      if (part.p.length) chan.current?.send('live', { live: part });
    };
    if (!live) {
      clearTimeout(liveTimer.current);
      flush();
      return;
    }
    if (liveTimer.current) return;
    liveTimer.current = setTimeout(flush, Math.max(0, LIVE_MS - (Date.now() - liveAt.current)));
  }, []);
  useEffect(() => () => clearTimeout(liveTimer.current), []);

  return {
    available,
    peers,
    connected,
    joined,
    sendInk,
    sendLive,
  };
}
